import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Button, Form, Alert, Container, Row, Col } from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './SigningPage.css';

const SigningPage = () => {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [clientName, setClientName] = useState('');
  const [typedSignature, setTypedSignature] = useState('');
  const [agreeToSign, setAgreeToSign] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigning, setIsSigning] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [error, setError] = useState(null);
  
  // Optional initials canvas for additional security
  const initialsCanvas = useRef(null);

  // Determine if this is a change order
  const isChangeOrder = proposal?.documentType === 'change_order';

  useEffect(() => {
    fetchProposal();
  }, [token]);

  const fetchProposal = async () => {
    try {
      const response = await fetch(`/api/sign/${token}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to load document');
      }
      
      const data = await response.json();
      setProposal(data.proposal);
      setClientName(data.proposal.clientName || '');
      
      if (data.status === 'signed') {
        setIsSigned(true);
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const clearInitials = () => {
    if (initialsCanvas.current) {
      initialsCanvas.current.clear();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!clientName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!typedSignature.trim()) {
      toast.error('Please type your full name in the signature field');
      return;
    }
    
    if (!agreeToSign) {
      toast.error(`Please confirm that you agree to sign this ${isChangeOrder ? 'change order' : 'proposal'}`);
      return;
    }
    
    setIsSigning(true);
    
    try {
      // Create signature data object
      const signaturePayload = {
        clientName: clientName.trim(),
        typedSignature: typedSignature.trim(),
        signatureType: 'typed'
      };
      
      // Optionally include initials if provided
      if (initialsCanvas.current && !initialsCanvas.current.isEmpty()) {
        signaturePayload.initialsData = initialsCanvas.current.toDataURL('image/png');
      }
      
      const response = await fetch(`/api/sign/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(signaturePayload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to save signature');
      }
      
      const data = await response.json();
      
      setIsSigned(true);
      toast.success(`${isChangeOrder ? 'Change order' : 'Proposal'} signed successfully! Thank you.`);
    } catch (err) {
      console.error('Error saving signature:', err);
      toast.error(err.message || 'Failed to save signature');
    } finally {
      setIsSigning(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Upon Approval';
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <h3>Loading document...</h3>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (isSigned) {
    return (
      <Container className="mt-5">
        <Card className="text-center p-5">
          <Card.Body>
            <div className="mb-4">
              <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" width="100" height="100">
                <circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none" stroke="#28a745" strokeWidth="2"/>
                <path className="checkmark__check" fill="none" stroke="#28a745" strokeWidth="3" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
            <h2 className="text-success mb-3">
              {isChangeOrder ? 'Change Order' : 'Proposal'} Signed Successfully!
            </h2>
            <p className="lead">Thank you for signing the {isChangeOrder ? 'change order' : 'proposal'}.</p>
            <p>You will receive a copy of the signed document via email shortly.</p>
            <p className="text-muted mt-4">You can now close this window.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Render Change Order content
  if (isChangeOrder) {
    return (
      <Container className="signing-page mt-4 mb-5">
        <ToastContainer position="top-center" />
        
        <Card className="mb-4">
          <Card.Header className="bg-warning text-dark">
            <h3 className="mb-0">Change Order {proposal.changeOrderNumber} - {proposal.projectName}</h3>
          </Card.Header>
          <Card.Body>
            {/* Client Information */}
            <div className="mb-4">
              <h5>Client Information</h5>
              <p className="mb-1"><strong>{proposal.clientName}</strong></p>
              {proposal.clientAddress && <p className="mb-0">{proposal.clientAddress}</p>}
              {proposal.clientCity && proposal.clientState && (
                <p className="mb-0">{proposal.clientCity}, {proposal.clientState} {proposal.clientZipCode}</p>
              )}
              {proposal.clientPhone && <p className="mb-0">Phone: {proposal.clientPhone}</p>}
              {proposal.clientEmail && <p className="mb-0">Email: {proposal.clientEmail}</p>}
            </div>

            {/* Reason for Change Order */}
            <div className="mb-4">
              <h5>Reason for Change Order</h5>
              <Card className="bg-light">
                <Card.Body>
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{proposal.description}</p>
                </Card.Body>
              </Card>
            </div>

            {/* Additional Work to be Performed */}
            {proposal.workItems && proposal.workItems.length > 0 && (
              <div className="mb-4">
                <h5>Additional Work to be Performed</h5>
                <Card>
                  <Card.Body>
                    <ul className="mb-0">
                      {proposal.workItems.map((item, index) => (
                        <li key={index} style={{ marginBottom: '0.5rem' }}>{item}</li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </div>
            )}

            {/* Cost & Payment */}
            <div className="mb-4">
              <h5>Cost & Payment</h5>
              <div className="p-3 bg-light rounded">
                <Row>
                  <Col md={6}>
                    <h4 className="text-primary mb-2">
                      Change Order Amount: {formatCurrency(proposal.amount)}
                    </h4>
                  </Col>
                  <Col md={6}>
                    <p className="mb-0">
                      <strong>Payment Due:</strong> {formatDate(proposal.paymentDueDate)}
                    </p>
                  </Col>
                </Row>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-4">
              <h5>Terms and Conditions</h5>
              <ul>
                <li>This change order becomes part of the original contract upon signature.</li>
                <li>Payment is due on the date specified above.</li>
                <li>Work will commence upon approval of this change order.</li>
                <li>All original contract terms remain in effect except as modified herein.</li>
                <li>This change order is subject to the same warranty provisions as the original contract.</li>
              </ul>
            </div>
          </Card.Body>
        </Card>

        {/* Signature Section */}
        <Card>
          <Card.Header className="bg-success text-white">
            <h4 className="mb-0">Sign Change Order</h4>
          </Card.Header>
          <Card.Body>
            <Alert variant="info">
              <strong>Agreement:</strong> By signing below, you authorize the additional work described above
              and agree to pay the specified amount according to the payment terms.
            </Alert>

            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Your Full Name *</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Enter your full name"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Date</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={new Date().toLocaleDateString()}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-4">
                <Form.Label><strong>Type Your Full Name as Signature *</strong></Form.Label>
                <Form.Control
                  type="text"
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder="Type your full legal name"
                  required
                  className="mb-2"
                />
                {typedSignature && (
                  <div className="signature-preview p-4 border rounded bg-light text-center">
                    <div 
                      style={{
                        fontFamily: "'Brush Script MT', 'Brush Script Std', cursive",
                        fontSize: '36px',
                        color: '#000',
                        fontWeight: 'normal'
                      }}
                    >
                      {typedSignature}
                    </div>
                    <small className="text-muted">This is how your signature will appear</small>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Check
                  type="checkbox"
                  id="agree-checkbox"
                  checked={agreeToSign}
                  onChange={(e) => setAgreeToSign(e.target.checked)}
                  required
                  label={
                    <span>
                      <strong>I agree that my typed name above constitutes my legal signature</strong> and I authorize the additional work and payment terms outlined in this change order.
                    </span>
                  }
                />
              </Form.Group>

              {/* Optional Initials Section */}
              <Form.Group className="mb-3">
                <Form.Label><strong>Initials (Optional)</strong></Form.Label>
                <p className="text-muted small mb-2">
                  You may optionally add your initials below using your mouse or touchscreen.
                </p>
                <div className="signature-canvas-container">
                  <SignatureCanvas
                    ref={initialsCanvas}
                    canvasProps={{
                      className: 'signature-canvas',
                      width: 500,
                      height: 100
                    }}
                  />
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={clearInitials}
                  className="mt-2"
                >
                  Clear Initials
                </Button>
              </Form.Group>

              <div className="d-grid gap-2">
                <Button
                  type="submit"
                  variant="success"
                  size="lg"
                  disabled={isSigning}
                >
                  {isSigning ? 'Signing...' : 'Sign Change Order'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        <div className="text-center mt-4 text-muted">
          <small>This link expires on {new Date(proposal.expiresAt).toLocaleDateString()}</small>
        </div>
      </Container>
    );
  }

  // Render Proposal content (original code)
  return (
    <Container className="signing-page mt-4 mb-5">
      <ToastContainer position="top-center" />
      
      <Card className="mb-4">
        <Card.Header className="bg-primary text-white">
          <h3 className="mb-0">Project Proposal - {proposal.projectName}</h3>
        </Card.Header>
        <Card.Body>
          {/* Client Information */}
          <div className="mb-4">
            <h5>Client Information</h5>
            <p className="mb-1"><strong>{proposal.clientName}</strong></p>
            {proposal.clientAddress && <p className="mb-0">{proposal.clientAddress}</p>}
            {proposal.clientCity && proposal.clientState && (
              <p className="mb-0">{proposal.clientCity}, {proposal.clientState} {proposal.clientZipCode}</p>
            )}
            {proposal.clientPhone && <p className="mb-0">Phone: {proposal.clientPhone}</p>}
            {proposal.clientEmail && <p className="mb-0">Email: {proposal.clientEmail}</p>}
          </div>

          {/* Scopes of Work */}
          {proposal.scopes && proposal.scopes.length > 0 && (
            <div className="mb-4">
              <h5>Scope of Work</h5>
              {proposal.scopes.map((scope) => (
                <Card key={scope.number} className="mb-3">
                  <Card.Header>
                    <strong>Scope {scope.number}: {scope.name}</strong>
                  </Card.Header>
                  <Card.Body>
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                      {scope.description}
                    </pre>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}

          {/* Project Cost */}
          <div className="mb-4">
            <h5>Project Cost</h5>
            <div className="p-3 bg-light rounded">
              <h4 className="text-primary mb-0">
                Total Project Cost: {formatCurrency(proposal.totalCost)}
              </h4>
            </div>
          </div>

          {/* Payment Schedule */}
          {(proposal.deposit || proposal.payments?.length > 0 || proposal.finalPayment) && (
            <div className="mb-4">
              <h5>Payment Schedule</h5>
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Payment Type</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th>Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal.deposit && (
                    <tr>
                      <td><strong>Deposit</strong></td>
                      <td>{formatCurrency(proposal.deposit)}</td>
                      <td>Initial payment to begin work</td>
                      <td>Upon signing</td>
                    </tr>
                  )}
                  {proposal.payments && proposal.payments.map((payment, index) => (
                    <tr key={index}>
                      <td><strong>Payment {index + 1}</strong></td>
                      <td>{formatCurrency(payment.Amount)}</td>
                      <td>{payment.Description || `Payment ${payment.PaymentOrder}`}</td>
                      <td>{payment.DueDate ? new Date(payment.DueDate).toLocaleDateString() : 'TBD'}</td>
                    </tr>
                  ))}
                  {proposal.finalPayment && (
                    <tr>
                      <td><strong>Final Payment</strong></td>
                      <td>{formatCurrency(proposal.finalPayment)}</td>
                      <td>Final payment upon completion</td>
                      <td>Upon completion</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="mb-4">
            <h5>Terms and Conditions</h5>
            <ul>
              <li>All payments are due on the dates specified above.</li>
              <li>Late payments may result in project delays.</li>
              <li>Final payment is due upon completion of all work.</li>
              <li>This is an estimate based on the initial consultation.</li>
              <li>Does not include unforeseen price increases or additional labor/materials.</li>
              <li>Any changes to the scope of work will require a written change order.</li>
              <li>Permits will be obtained as required.</li>
            </ul>
          </div>
        </Card.Body>
      </Card>

      {/* Signature Section */}
      <Card>
        <Card.Header className="bg-success text-white">
          <h4 className="mb-0">Sign Proposal</h4>
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            <strong>Agreement:</strong> By signing below, the client agrees to all terms, conditions, 
            scope of work, payment schedule, and pricing outlined in this proposal.
          </Alert>

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Your Full Name *</strong></Form.Label>
                  <Form.Control
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Date</strong></Form.Label>
                  <Form.Control
                    type="text"
                    value={new Date().toLocaleDateString()}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label><strong>Type Your Full Name as Signature *</strong></Form.Label>
              <Form.Control
                type="text"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder="Type your full legal name"
                required
                className="mb-2"
              />
              {typedSignature && (
                <div className="signature-preview p-4 border rounded bg-light text-center">
                  <div 
                    style={{
                      fontFamily: "'Brush Script MT', 'Brush Script Std', cursive",
                      fontSize: '36px',
                      color: '#000',
                      fontWeight: 'normal'
                    }}
                  >
                    {typedSignature}
                  </div>
                  <small className="text-muted">This is how your signature will appear</small>
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                id="agree-checkbox"
                checked={agreeToSign}
                onChange={(e) => setAgreeToSign(e.target.checked)}
                required
                label={
                  <span>
                    <strong>I agree that my typed name above constitutes my legal signature</strong> and I intend to be legally bound by this proposal, including all terms, conditions, scope of work, and payment schedule outlined above.
                  </span>
                }
              />
            </Form.Group>

            {/* Optional Initials Section */}
            <Form.Group className="mb-3">
              <Form.Label><strong>Initials (Optional)</strong></Form.Label>
              <p className="text-muted small mb-2">
                You may optionally add your initials below using your mouse or touchscreen.
              </p>
              <div className="signature-canvas-container">
                <SignatureCanvas
                  ref={initialsCanvas}
                  canvasProps={{
                    className: 'signature-canvas',
                    width: 500,
                    height: 100
                  }}
                />
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={clearInitials}
                className="mt-2"
              >
                Clear Initials
              </Button>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button
                type="submit"
                variant="success"
                size="lg"
                disabled={isSigning}
              >
                {isSigning ? 'Signing...' : 'Sign Proposal'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <div className="text-center mt-4 text-muted">
        <small>This link expires on {new Date(proposal.expiresAt).toLocaleDateString()}</small>
      </div>
    </Container>
  );
};

export default SigningPage;
